import { useEffect, useRef, useState } from 'react';

const features = [
  { icon: 'ri-apple-fill', label: 'iOS Native' },
  { icon: 'ri-shield-check-line', label: 'Security First' },
  { icon: 'ri-speed-up-line', label: 'Performance' },
  { icon: 'ri-palette-line', label: 'UI/UX Design' },
  { icon: 'ri-code-s-slash-line', label: 'Clean Code' },
];

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={ref}
      className="w-full bg-gray-950 py-24 md:py-36 px-6 md:px-10"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Top Label */}
        <p
          className={`text-gray-500 text-xs tracking-[0.4em] uppercase mb-8 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Company Overview
        </p>

        {/* Main Title */}
        <div
          className={`transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2
            className="text-white mb-16 md:mb-20"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: 'clamp(1.8rem, 3.5vw, 3.2rem)',
              lineHeight: 1.35,
              maxWidth: '820px',
            }}
          >
            We craft&nbsp;
            <em style={{ fontStyle: 'italic', color: '#d1d5db' }}>
              powerful iOS experiences
            </em>
            &nbsp;that put security and elegance at the center of every product we build
            for millions of mobile users worldwide.
          </h2>
        </div>

        {/* Two-column text */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-10 mb-20 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-gray-400 text-sm md:text-base leading-relaxed font-300" style={{ fontWeight: 300 }}>
            MELTI SRL is a dedicated iOS development team focused on building high-quality
            native applications. Our products address real-world challenges: protecting
            user privacy with VPN technology and keeping devices running smoothly with
            intelligent cleaning tools.
          </p>
          <p className="text-gray-400 text-sm md:text-base leading-relaxed font-300" style={{ fontWeight: 300 }}>
            Every application we release goes through rigorous testing and design
            refinement to ensure it meets the highest standards of performance and
            usability. We believe great software should feel effortless to use.
          </p>
        </div>

        {/* Feature Cards */}
        <div
          className={`flex flex-wrap gap-4 transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {features.map((feat, i) => (
            <div
              key={feat.label}
              className="flex items-center gap-3 bg-gray-800/60 rounded-xl px-5 py-4 flex-1 min-w-[140px]"
              style={{ transitionDelay: `${300 + i * 60}ms` }}
            >
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`${feat.icon} text-white text-xl`}></i>
              </div>
              <span className="text-white text-sm font-500 whitespace-nowrap" style={{ fontWeight: 500 }}>
                {feat.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
